import { VPadP2PAction, VPadP2PActionType } from "./P2P/p2p_actions"
import { VPadP2PProtocol } from "./P2P/p2p_protocol"
import { VPadP2PProtocolV1 } from "./P2P/v1/VPadP2PProtocolV1"
import { PadSessionManager } from "./PadSessionManager"

import {
  StandardGamepadAxisIndex,
  StandardGamepadButtonIndex,
  VPadStage,
} from '@/interfaces/virtualPad_types'

export interface VPPeerConnectionHandlers {
  onButtonChanged: (
    buttonIndex: StandardGamepadButtonIndex,
    value: number
  ) => void

  onAxisChanged: (
    axisIndex: StandardGamepadAxisIndex,
    value: number
  ) => void

  onStatusChanged: (status: VPadStage) => void

  onRemoteClose: () => void

  onDisconnected: () => void
}

/**
 * Manages the WebRTC connection associated with a virtual pad
 */
export class VPPeerConnection {
  private static readonly WATCHDOG_TIME = 20

  private peerConnection: RTCPeerConnection | undefined
  private dataChannel: RTCDataChannel | undefined
  private readonly pendingIceCandidates: RTCIceCandidateInit[] = []
  private isClosing = false

  private readonly protocol: VPadP2PProtocol

  /**
   * Initializes the virtual pad peer connection
   *
   * @param sessionId Session identifier associated with the virtual pad
   * @param connectionId Connection identifier of the virtual pad
   * @param handlers Callbacks triggered by P2P events and connection changes
   */
  public constructor(
    private readonly sessionId: string,
    private readonly connectionId: string,
    private readonly handlers: VPPeerConnectionHandlers
  ) {
    this.protocol = new VPadP2PProtocolV1()
  }

  /**
   * Initializes and establishes the WebRTC connection
   * 
   * The connection is considered established when the data channel
   * is open and ready to exchange data
   *
   * @throws If the WebRTC connection or data channel cannot be established
   */
  public async connect(certificate?: RTCCertificate[]): Promise<void> {
    if (this.peerConnection !== undefined) {
      throw new Error(
        `VPPEERCONNECTION :: CONNECT :: The peer connection already exists`
      )
    }

    const server = PadSessionManager.getInstance().getServerConnection().getServer()

    const config: RTCConfiguration = {
      certificates: certificate,
      bundlePolicy: 'max-bundle',
      iceServers: [
        { urls: ['stun:stun.l.google.com:19302'] },
        /*
        { urls: [`stun:${server}:3478`] },
        {
          urls: `turn:${server}:3478`,
          username: '1830297600',
          credential: 'soQ5kv5gWwPwF2bZcqKZbDqds0k='
        }
        */
      ]
    }

    this.peerConnection = new RTCPeerConnection(config)

    try {
      this.peerConnection.addEventListener('icecandidate', this.handleLocalIceCandidate)

      this.dataChannel = this.peerConnection.createDataChannel('vpad-inputs')
      this.dataChannel.binaryType = 'arraybuffer'
      this.configureDataChannel(this.dataChannel)

      await this.createAndSendOffer()
      await this.waitUntilDataChannelOpen()
    } catch (error) {
      this.close()
      throw error
    }
  }

  /**
   * Configures the data channel event handlers
   *
   * @param dataChannel Data channel associated with the virtual pad
   */
  private configureDataChannel(dataChannel: RTCDataChannel): void {
    dataChannel.addEventListener('message', (e) => {
      this.handleMessage(e.data)
    })

    dataChannel.addEventListener('close', () => {
      if (this.isClosing) return
      this.handlers.onDisconnected()
    })

    dataChannel.addEventListener('error', (e) => {
      console.error(`VPPEERCONNECTION :: DATA CHANNEL :: Error`,e)
    })
  }

  /**
   * Handles raw data received through the virtual pad DataChannel.
   *
   * The received data is parsed according to the active P2P protocol
   * and converted into an internal virtual pad action.
   *
   * @param data Raw data received through the DataChannel
   */
  private handleMessage(data: unknown): void {
    try {
      const message = this.protocol.parse(data)
      const action = this.protocol.getAction(message)
      this.handleAction(action)
    } catch (error) {
      console.error(
        `VPPEERCONNECTION :: HANDLE MESSAGE :: Invalid message received from virtual pad ${this.connectionId}`,
        error
      )
    }
  }

  /**
   * Executes an internal virtual pad action by triggering the
   * corresponding connection handler.
   *
   * @param action Virtual pad action to execute
   */
  private handleAction(action: VPadP2PAction): void {
    // console.log('ACTION')
    // console.log(action)
    switch (action.type) {
      case VPadP2PActionType.BUTTON: {
        this.handlers.onButtonChanged(action.buttonIndex, action.value)
        break
      }

      case VPadP2PActionType.MOVEMENT: {
        for (const axis of action.axes) {
          this.handlers.onAxisChanged(
            axis.index,
            axis.value
          )
        }
        break
      }

      case VPadP2PActionType.PING: {
        this.sendPong(action.sentAt)
        break
      }

      case VPadP2PActionType.STATUS: {
        this.handlers.onStatusChanged(action.status)
        break
      }

      case VPadP2PActionType.CLOSE: {
        this.handlers.onRemoteClose()
        break
      }

      default: {
        console.warn(`VPPEERCONNECTION :: HANDLE ACTION :: Unknown action:`, action)
      }
    }
  }

  /**
   * Creates, applies and sends the local WebRTC offer
   *
   * @throws If the offer cannot be created, applied or sent
   */
  private async createAndSendOffer(): Promise<void> {
    const peerConnection = this.peerConnection

    if (peerConnection === undefined) 
      throw new Error(`VPPEERCONNECTION :: CREATEANDSENDOFFER :: The peer connection doesn't exist`)

    const offer = await peerConnection.createOffer()
    await peerConnection.setLocalDescription(offer)
    const localDescription = peerConnection.localDescription

    if (localDescription === null)
      throw new Error(`VPPEERCONNECTION :: CREATEANDSENDOFFER :: The local description doesn't exist`)

    PadSessionManager
      .getInstance()
      .getServerConnection()
      .getSessionProtocol()
      .sendOffer(this.connectionId, localDescription.sdp)
  }

  /**
   * Sends a local ICE candidate to the remote virtual pad
   *
   * @param e Local ICE candidate event
   */
  private readonly handleLocalIceCandidate = (e: RTCPeerConnectionIceEvent): void => {
    if (e.candidate === null) return

    PadSessionManager
      .getInstance()
      .getServerConnection()
      .getSessionProtocol()
      .sendIceCandidate(this.connectionId, e.candidate.toJSON())
  }

  /**
   * Waits until the data channel is open and ready to exchange data
   *
   * @throws If the peer connection fails or closes, the data channel closes,
   * or the connection timeout expires before the channel opens
   */
  private waitUntilDataChannelOpen(): Promise<void> {
    return new Promise((resolve, reject) => {
      const peerConnection = this.peerConnection
      const dataChannel = this.dataChannel

      if (peerConnection === undefined || dataChannel === undefined) {
        reject(new Error(`VPPEERCONNECTION :: WAITUNTILDATACHANNELOPEN  :: Connection resources don't exist`))
        return
      }

      const handleOpen = (): void => {
        cleanup()
        resolve()
      }

      const handleClose = (): void => {
        cleanup()
        reject(new Error(`VPPEERCONNECTION :: WAITUNTILDATACHANNELOPEN  :: The data channel closed before opening`))
      }

      const handleError = (): void => {
        cleanup()
        reject(new Error(`VPPEERCONNECTION :: WAITUNTILDATACHANNELOPEN  :: The data channel failed before opening`))
      }

      const handleConnectionStateChange = (): void => {
        if (peerConnection.connectionState === 'failed' || peerConnection.connectionState === 'closed') {
          cleanup()
          reject(new Error(`VPPEERCONNECTION :: WAITUNTILDATACHANNELOPEN  :: Connection changed to "${peerConnection.connectionState}"`))
        }
      }

      const cleanup = (): void => {
        window.clearTimeout(watchDogTimer)
        dataChannel.removeEventListener('open', handleOpen)
        dataChannel.removeEventListener('close', handleClose)
        dataChannel.removeEventListener('error', handleError)
        peerConnection.removeEventListener('connectionstatechange', handleConnectionStateChange)
      }

      const watchDogTimer = window.setTimeout(() => {
        cleanup()
        reject(new Error(`VPPEERCONNECTION :: WAITUNTILDATACHANNELOPEN  :: Connection timed out for vpad ${this.getConnectionId()}`))
      }, 1000 * VPPeerConnection.WATCHDOG_TIME)      

      dataChannel.addEventListener('open', handleOpen)
      dataChannel.addEventListener('close', handleClose)
      dataChannel.addEventListener('error', handleError)
      peerConnection.addEventListener('connectionstatechange', handleConnectionStateChange)

      if (dataChannel.readyState === 'open') {
        handleOpen()
        return
      }
      
      // In case it was already connected before the listener was added
      handleConnectionStateChange()
    })
  }

  /**
   * Applies the WebRTC answer received from the remote virtual pad
   *
   * @param sdp Remote answer SDP
   * @throws If the peer connection doesn't exist or the answer cannot be applied
   */
  public async setAnswer(sdp: string): Promise<void> {
    const peerConnection = this.peerConnection

    if (peerConnection === undefined)
      throw new Error(`VPPEERCONNECTION :: SET ANSWER :: The peer connection doesn't exist`)

    if (peerConnection.signalingState !== 'have-local-offer')
      throw new Error(`VPPEERCONNECTION :: SET ANSWER :: Cannot apply the answer while signaling state is "${peerConnection.signalingState}"`)

    await peerConnection.setRemoteDescription({ type: 'answer', sdp })

    await this.flushPendingIceCandidates()
  }

  /**
   * Adds an ICE candidate received from the remote virtual pad
   *
   * Candidates received before the answer are queued until the remote
   * description has been applied.
   *
   * @param candidate Remote ICE candidate
   * @throws If the peer connection doesn't exist
   */
  public async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    const peerConnection = this.peerConnection

    if (peerConnection === undefined)
      throw new Error(`VPPEERCONNECTION :: ADDICECANDIDATE :: The peer connection doesn't exist`)

    if (peerConnection.remoteDescription === null) {
      this.pendingIceCandidates.push(candidate)
      return
    }

    await peerConnection.addIceCandidate(candidate)
  }

  /**
   * Adds the remote ICE candidates queued before receiving the answer
   */
  private async flushPendingIceCandidates(): Promise<void> {
    const peerConnection = this.peerConnection

    if (peerConnection === undefined || peerConnection.remoteDescription === null)
      return

    while (this.pendingIceCandidates.length > 0) {
      const candidate = this.pendingIceCandidates.shift()
      if (candidate !== undefined) await peerConnection.addIceCandidate(candidate)
    }
  }

  /**
   * Closes the WebRTC connection.
   */
  public close(): void {
    if (this.isClosing) return
    this.isClosing = true

    this.peerConnection?.removeEventListener('icecandidate', this.handleLocalIceCandidate)

    this.pendingIceCandidates.length = 0

    this.dataChannel?.close()
    this.peerConnection?.close()
    
    this.dataChannel = undefined
    this.peerConnection = undefined
  }

  /**
   * Sends data through the virtual pad data channel
   *
   * @param data Data to send
   * @throws If the data channel is not open
   */
  public send(data: string): void {
    if (this.dataChannel?.readyState !== 'open')
      throw new Error(`VPPEERCONNECTION :: SEND :: The data channel isn't open`)

    this.dataChannel.send(data)
  }

  /**
   * Notifies the remote virtual pad that it has been ejected
   * by the host.
   *
   * @throws If the DataChannel is not open
   */
  public sendEject(): void {
    const message = this.protocol.createEjectMessage()
    this.send(this.protocol.encode(message))
  }

  /**
   * Notifies the remote virtual pad that it has been manually removed by the host
   *
   * @throws If the DataChannel is not open
   */
  public sendLeave(): void {
    // console.log('VPAD PEER CONNECTION :: SEND LEAVE')
    const message = this.protocol.createLeaveMessage()
    this.send(this.protocol.encode(message))
  }

  /**
   * Responds to a ping received from the remote virtual pad
   *
   * @throws If the DataChannel is not open
   */
  private sendPong(sentAt: string): void {
    const message = this.protocol.createPongMessage(sentAt)
    this.send(this.protocol.encode(message))
  }

  /**
   * Returns whether the data channel is ready to exchange data.
   *
   * @returns True if the data channel is open
   */
  public isConnected(): boolean {
    return this.dataChannel?.readyState === 'open'
  }

  /**
   * Returns the session identifier associated with the connection.
   *
   * @returns The session identifier
   */
  public getSessionId(): string {
    return this.sessionId
  }

  /**
   * Returns the virtual pad connection identifier.
   *
   * @returns The connection identifier
   */
  public getConnectionId(): string {
    return this.connectionId
  }
}